import { initializeApp, type FirebaseApp } from 'firebase/app';
import {
  connectAuthEmulator,
  createUserWithEmailAndPassword,
  getAuth,
  GoogleAuthProvider,
  onIdTokenChanged,
  sendEmailVerification,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  updateProfile,
  type Auth,
} from 'firebase/auth';
import type { AuthAdapter, AuthUser } from '@/auth/types';
import { env } from './env';

let app: FirebaseApp | null = null;
let auth: Auth | null = null;

/** Lazily initialise Firebase (never at import time, so tests and public pages stay light). */
export function firebaseAuth(): Auth {
  if (auth) return auth;
  app = initializeApp({
    apiKey: env.firebase.apiKey,
    projectId: env.firebase.projectId,
    authDomain: env.firebase.authDomain,
  });
  auth = getAuth(app);
  if (env.firebase.authEmulatorHost) {
    connectAuthEmulator(auth, `http://${env.firebase.authEmulatorHost}`, { disableWarnings: true });
  }
  return auth;
}

function toUser(u: import('firebase/auth').User | null): AuthUser | null {
  if (!u) return null;
  return {
    uid: u.uid,
    email: u.email,
    displayName: u.displayName,
    photoURL: u.photoURL,
    emailVerified: u.emailVerified,
  };
}

/** Production adapter backed by Firebase Auth (emulator in dev). */
export const firebaseAuthAdapter: AuthAdapter = {
  subscribe(cb) {
    return onIdTokenChanged(firebaseAuth(), (u) => cb(toUser(u)));
  },
  async getIdToken(forceRefresh) {
    const u = firebaseAuth().currentUser;
    return u ? u.getIdToken(forceRefresh) : null;
  },
  async signIn(email, password) {
    await signInWithEmailAndPassword(firebaseAuth(), email, password);
  },
  async register(email, password, displayName) {
    const cred = await createUserWithEmailAndPassword(firebaseAuth(), email, password);
    if (displayName) await updateProfile(cred.user, { displayName });
    await cred.user.getIdToken(true);
  },
  async signInWithGoogle() {
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({ prompt: 'select_account' });
    await signInWithPopup(firebaseAuth(), provider);
  },
  async resetPassword(email) {
    await sendPasswordResetEmail(firebaseAuth(), email);
  },
  async sendEmailVerification() {
    const u = firebaseAuth().currentUser;
    if (u) await sendEmailVerification(u, { url: window.location.href });
  },
  async reloadUser() {
    const u = firebaseAuth().currentUser;
    if (!u) return;
    await u.reload();
    // The API reads `email_verified` from the ID token, so mint a new one (also notifies subscribers).
    await u.getIdToken(true);
  },
  async signOut() {
    await signOut(firebaseAuth());
  },
};

/** Map Firebase error codes to i18n keys under `auth:errors.*`. */
export function authErrorKey(err: unknown): string {
  const code = (err as { code?: string })?.code ?? '';
  switch (code) {
    case 'auth/invalid-credential':
    case 'auth/wrong-password':
    case 'auth/user-not-found':
    case 'auth/invalid-login-credentials':
      return 'errors.invalidCredentials';
    case 'auth/email-already-in-use':
      return 'errors.emailInUse';
    case 'auth/weak-password':
      return 'errors.weakPassword';
    case 'auth/invalid-email':
      return 'errors.invalidEmail';
    case 'auth/too-many-requests':
      return 'errors.tooManyRequests';
    case 'auth/popup-closed-by-user':
    case 'auth/cancelled-popup-request':
      return 'errors.popupClosed';
    case 'auth/network-request-failed':
      return 'errors.network';
    default:
      return 'errors.generic';
  }
}
